import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { JsonLd, useSEO } from '../utils/seo';

/**
 * Project guide detail page — /projects/:slug.
 *
 * This used to be an index-plus-accordion: every route rendered all eight
 * guides with one expanded, which meant eight URLs served near-identical
 * markup and Google had no reason to treat them as distinct pages. Each route
 * now renders ONE guide, top of page, with the other guides as a link grid at
 * the bottom. The browsable index moved to /projects.
 */
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const projectDescription = project ? `${project.tagline} See recommended materials, depths, and practical tips for a ${project.name.toLowerCase()} project in the Puget Sound area.` : 'Explore practical gravel, drainage, patio, and landscaping project guides from 206Gravel.';
  useSEO({
    title: project ? `${project.name} — Materials, Depths & Pro Tips | 206Gravel` : 'Gravel Project Guides | 206Gravel',
    description: projectDescription,
    path: project ? `/projects/${project.slug}` : '/projects'
  });
  if (!project) {
    return <Navigate to="/projects" replace data-id="element-3926" data-mp="2lur71hrmdb3p" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  const otherProjects = projects.filter(p => p.slug !== project.slug);
  return <main className="min-h-screen bg-[#F2EFE8] dark:bg-[#262626] pt-8 pb-24" data-id="element-3927" data-mp="3bmuik72fwdsp">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: project.name,
      description: projectDescription,
      url: `https://206gravel.com/projects/${project.slug}`,
      supply: project.materials.map(material => {
        const product = getProductDetails(material.slug);
        return {
          '@type': 'HowToSupply',
          name: product ? product.name : material.role,
          ...(product ? {
            url: `https://206gravel.com/shop/${product.slug}`,
            image: product.image
          } : {})
        };
      }),
      step: project.tips.map((tip, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: `Step ${index + 1}`,
        text: tip,
        url: `https://206gravel.com/projects/${project.slug}#step-${index + 1}`
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: project.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Project Guides',
        item: 'https://206gravel.com/projects'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: project.name,
        item: `https://206gravel.com/projects/${project.slug}`
      }]
    }]} data-id="element-3928" data-mp="3go1qq78hvezw" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3929" data-mp="150eyc20jd255">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3930" data-mp="101bv8n4495zt">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3931" data-mp="2ovsvhyrpfcik">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3932" data-mp="2tmhoxjmy2jkc" />
          <Link to="/projects" className="hover:text-brand-orange transition-colors whitespace-nowrap" data-id="element-3933" data-mp="2to2ltfwqeeld">
            Project Guides
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3934" data-mp="2jxhpy08fzo65" />
          <span className="text-stone-900 dark:text-white font-medium truncate" data-id="element-3935" data-mp="37m0x2cfr9uwn">
            {project.name}
          </span>
        </nav>

        {/* Header */}
        <div className="mb-12" data-id="element-3936" data-mp="3cklopngxz69h">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3937" data-mp="2xnuwpz9lkwtw">
            <Icon className="w-8 h-8" data-id="element-3938" data-mp="32m4r47nhzteg" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3939" data-mp="32oqzg9i7qcxq">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3940" data-mp="1r8ty1lt6u0jm">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3941" data-mp="9c8om9xqx7e3">
            <p data-id="element-3942" data-mp="4df9ye6q3acc">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3943" data-mp="i9srtof65drc">
            <span className="mr-2" data-id="element-3944" data-mp="3mptzrqk3wj31">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3945" data-mp="3hq7h31lieamu">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3946" data-mp="3qqvz07e0wv50">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3947" data-mp="3qpkuu6go1ldd">
            Recommended Materials
          </h2>
          {/* Materials read as an ordered SPEC LIST, not cards — square edges,
              shared rules between rows, a step number, and a small square
              thumbnail make these read as the layers of one job. */}
          <div className="border-t border-stone-300 dark:border-white/15" data-id="element-3948" data-mp="16xn7uvguiz99">
            {project.materials.map((mat, idx) => {
            const product = getProductDetails(mat.slug);
            if (!product) return null;
            return <motion.div key={mat.slug} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.06
            }} className="flex gap-4 border-b border-stone-300 py-6 sm:gap-6 dark:border-white/15" data-id="element-3949" data-mp="1gmol4dw0b2jj" data-mpi={idx}>
                  <div className="flex shrink-0 flex-col items-center gap-3" data-id="element-3950" data-mp="t08c79001v8u">
                    <span className="flex h-7 w-7 items-center justify-center bg-stone-900 text-xs font-bold text-white dark:bg-white dark:text-stone-900" data-id="element-3951" data-mp="xytdlbqzmkyv">
                      {idx + 1}
                    </span>
                    <img src={product.image} alt={product.name} loading="lazy" decoding="async" className="h-20 w-20 object-cover sm:h-24 sm:w-24" data-id="element-3952" data-mp="29akgnf9uaoeq" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3953" data-mp="3owecugzvg64x">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-3954" data-mp="3k4mtorj1p2ot">
                      <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white" data-id="element-3955" data-mp="3f5jkqvzy2cbk">
                        {product.name}
                      </h3>
                      <div className="font-heading text-lg font-bold tabular-nums text-stone-900 dark:text-white" data-id="element-3956" data-mp="3a7rus7iv10a0">
                        ${product.pricePerYard}
                        <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3957" data-mp="314z5g9t8mymt">
                          /yd
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold uppercase tracking-wider" data-id="element-3958" data-mp="zdcgsewtjfti">
                      <span className="text-brand-orange" data-id="element-3959" data-mp="ud80opzton3t">{mat.role}</span>
                      <span className="text-stone-300 dark:text-stone-600" data-id="element-3960" data-mp="3agt51brthzr">·</span>
                      <span className="text-stone-500 dark:text-stone-400" data-id="element-3961" data-mp="8807004k3sr6">
                        Depth: {mat.depth}
                      </span>
                    </div>
                    <p className="mt-2.5 text-stone-600 dark:text-stone-300" data-id="element-3962" data-mp="3uocpp5go9g2h">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3963" data-mp="3um9vsacef2sh">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>
                    <Link to={`/shop/${product.slug}`} className="mt-3 inline-flex items-center text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange/80" data-id="element-3964" data-mp="36qoze9lheyos">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3965" data-mp="14yskqr40p8ps" />
                    </Link>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3966" data-mp="2ugftvdboklya">
          {/* Pro Tips */}
          <div data-id="element-3967" data-mp="2ued0qkf8ecpc">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3968" data-mp="2phd0iq52c465">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3969" data-mp="346r9vy8n2llj" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3970" data-mp="2zf95f07eq2vt">
              {project.tips.map((tip, idx) => <li key={idx} id={`step-${idx + 1}`} className="flex items-start scroll-mt-24" data-id="element-3971" data-mp="3d6xgr19u0nbq" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3972" data-mp="395c8jt88sx7v" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3973" data-mp="3n53ldqn0w7ka">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3974" data-mp="1sqkhbjmu1w05">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3975" data-mp="1ss5bh858jcxh">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3976" data-mp="1ntaqq0npjeaq" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3977" data-mp="2wlwx6aul8fu">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3978" data-mp="3u0kdoimmnmqy" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3979" data-mp="3u3olrr37r2mw">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3980" data-mp="3l170bevdue0y">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center" style={{
        backgroundColor: '#575656'
      }} data-id="element-3981" data-mp="gnqnzk05gsek">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3982" data-mp="bufm7xa731ti">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3983" data-mp="14c1j06j5zxux">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-3984" data-mp="zjgu9jf48ay9">
            <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3985" data-mp="2i9w5g43uupr2">
              Get an Estimate
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-3986" data-mp="2w8ch89gkr6g2" />
            </Link>
            <Link to="/calculator" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white border border-white/25 hover:bg-white/10 rounded-xl transition-colors" data-id="element-3987" data-mp="317feh7qbc8dh">
              Calculate how much
            </Link>
          </div>
        </div>

        {/* Other guides — the browsable index lives at /projects; this is the
            lateral path for someone who opened the wrong guide. */}
        <section className="mt-16" aria-label="Other project guides" data-id="element-3988" data-mp="3jygdtwvci5xk">
          <div className="flex items-baseline justify-between gap-4 mb-6" data-id="element-3989" data-mp="3owi698envhlp">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white" data-id="element-3990" data-mp="3oung9foj8zar">
              Other project guides
            </h2>
            <Link to="/projects" className="text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors whitespace-nowrap" data-id="element-3991" data-mp="1jw70q0d45nae">
              See all
            </Link>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3992" data-mp="1ttbna1avfkrq">
            {otherProjects.map(p => <li key={p.slug} data-id="element-3993" data-mp="27kgr7fl9uz7v">
                <Link to={`/projects/${p.slug}`} className="group flex items-start justify-between gap-4 h-full p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-3994" data-mp="27jf58g2pwsbd">
                  <span className="min-w-0" data-id="element-3995" data-mp="2ckcluo82xear">
                    <span className="block font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-3996" data-mp="2cle8zr2ddr8u">
                      {p.name}
                    </span>
                    <span className="mt-0.5 block text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3997" data-mp="i7wjhyiemkb6">
                      {p.tagline}
                    </span>
                    <span className="mt-1.5 block text-xs font-semibold text-stone-500 dark:text-stone-400" data-id="element-3998" data-mp="2yrgpw3e5lrfj">
                      {p.typicalYards}
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-3999" data-mp="2p1ng97xknmdx" />
                </Link>
              </li>)}
          </ul>
        </section>
      </div>
    </main>;
}